/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    useMemo,
    ReactNode,
    useState,
    useEffect,
    useCallback,
    useRef,
    HTMLProps,
    CSSProperties,
} from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
    flexRender,
    PaginationState,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    useReactTable,
    CellContext,
    ColumnDef as TanstackColumnDef,
    Cell,
    Row,
    createColumnHelper,
} from '@tanstack/react-table';
import { cn } from '../../utils';
import { useVirtualizer } from '@tanstack/react-virtual';
import Pagination from '../Pagination/Pagination';
import Spinner from '../Spinner/Spinner';

export { createColumnHelper };
export type { Cell, Row, CellContext, TanstackColumnDef, ColumnDef };

type MyColumnDef<TData, TUnknown> = ColumnDef<TData, TUnknown> & {
    accessorKey: string;
};

type ColumnDef<TData, TValue = unknown> = TanstackColumnDef<TData, TValue> & {
    id: string;
    accessorKey: string;
    header?: string;
    enableSorting?: boolean;
    sortable?: boolean;
    size?: number | string;
    cell?: (context: CellContext<TData, TValue>) => ReactNode;
    fixed?: 'left' | 'right' | undefined;
};

type TableProps<TData> = {
    data: TData[];
    columns?: ColumnDef<TData>[];
    page?: {
        status: boolean;
        pageSize?: number;
        showResults?: boolean;
        numSlots?: number;
    };
    className?: string;
    onRowsSelected?: (rows: TData[]) => void;
    loading?: boolean;
    height?: string | number;
    rowHeight?: string;
    draggable?: boolean;
    primaryField?: string;
    onColumnDragged?: (columns: ColumnDef<TData>[]) => void;
};

type Column = {
    getSize: () => number;
    getCanSort: () => boolean;
    getIsSorted: () => 'asc' | 'desc' | undefined;
    getToggleSortingHandler: () => React.MouseEventHandler<HTMLSpanElement>;
    columnDef: {
        header: string;
    };
};

type HeaderType = {
    id: string;
    colSpan: number;
    isPlaceholder: boolean;
    getContext: () => any;
    column: Column;
};

const getIconClassName = (
    sortDirection: 'asc' | 'desc',
    isSorted: string | undefined,
) =>
    cn('alli-stroke-2', {
        'alli-rounded-sm alli-bg-blue-gray-150': isSorted === sortDirection,
        'alli-text-gray-700': isSorted !== sortDirection,
    });

const getCommonStickyStyles = (
    column: any,
    tableContainerRef: any,
): CSSProperties => {
    const isPinned = column.columnDef.fixed;
    const leftPosition =
        isPinned === 'left' ? `${column.getStart('left')}px` : undefined;
    const rightPosition =
        isPinned === 'right' ? `${column.getAfter('right')}px` : undefined;

    const hasHorizontalScroll = () => {
        if (tableContainerRef.current) {
            return tableContainerRef.current.scrollLeft > 0;
        }
        return false;
    };

    const boxShadow = hasHorizontalScroll()
        ? isPinned === 'left'
            ? '-4px 0 4px -4px rgba(156, 163, 175,.50) inset '
            : isPinned === 'right'
              ? '4px 0 4px -4px rgba(156, 163, 175,.50) inset'
              : undefined
        : undefined;

    return {
        boxShadow,
        left: leftPosition,
        right: rightPosition,
        position: isPinned ? 'sticky' : 'inherit',
        width: column.getSize(),
        zIndex: isPinned ? 1 : 0,
    };
};

const renderHeaders = (
    headers: HeaderType[],
    tableInstance: any,
    enableRowSelelction: boolean,
    tableContainerRef: any,
    moveColumn: any,
    draggable: boolean,
) => {
    const nonDraggableHeaders = ['edit', 'select', 'menu', 'icon'];

    const handleHeaderCheckboxChange = () => {
        tableInstance.toggleAllRowsSelected(
            !(
                tableInstance.getIsAllRowsSelected() ||
                tableInstance.getIsSomeRowsSelected()
            ),
        );
    };

    let draggedColumnIndex = -1;

    const handleDragStart = (index: number) => (event: React.DragEvent) => {
        draggedColumnIndex = index;
        event.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = () => (event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const handleDrop =
        (index: number, draggable: boolean) => (event: React.DragEvent) => {
            event.preventDefault();
            if (
                !draggable ||
                draggedColumnIndex === -1 ||
                draggedColumnIndex === index
            ) {
                return;
            }

            moveColumn(draggedColumnIndex, index);
            draggedColumnIndex = -1;
        };

    return headers.map((header, index) => {
        const isSorted = header.column.getIsSorted();
        const canSort = header.column.getCanSort();
        const style = {
            width: header.column.getSize(),
            maxWidth: header.column.getSize(),
            minWidth: header.column.getSize(),
            ...getCommonStickyStyles(header.column, tableContainerRef),
        };

        const iconProps = {
            height: 12,
            width: 12,
            className: 'alli-stroke-2',
            style: {
                strokeWidth: 4,
                padding: 0.5,
            },
        };

        const isDraggable =
            draggable && !nonDraggableHeaders.includes(header.id);

        return (
            <th
                data-testid="Table-header"
                className={cn(
                    'alli-sm:pl-6 alli-bg-gray-100 alli-px-3 alli-py-3.5 alli-text-left alli-text-sm alli-font-medium alli-text-blue-gray-700',
                    {
                        'alli-cursor-pointer hover:alli-bg-blue-gray-100':
                            canSort,
                    },
                    isDraggable && 'alli-cursor-grab',
                )}
                key={header.id}
                colSpan={header.colSpan}
                style={style}
                onClick={
                    canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                }
                draggable={isDraggable}
                onDragStart={isDraggable ? handleDragStart(index) : undefined}
                onDragOver={isDraggable ? handleDragOver() : undefined}
                onDrop={handleDrop(index, isDraggable)}>
                <>
                    {enableRowSelelction && header.id === 'select' ? (
                        <IndeterminateCheckbox
                            {...{
                                checked: tableInstance.getIsAllRowsSelected(),
                                indeterminate:
                                    tableInstance.getIsSomeRowsSelected(),
                                onChange: handleHeaderCheckboxChange,
                            }}
                        />
                    ) : (
                        <div className="alli-flex alli-items-center">
                            <div
                                title={header.column.columnDef.header}
                                className="alli-overflow-hidden alli-text-ellipsis alli-text-nowrap">
                                {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                )}
                            </div>
                            {canSort && (
                                <div className="alli-ml-3 alli-cursor-pointer alli-items-center alli-font-bold alli-text-blue-gray-700">
                                    <ChevronUpIcon
                                        {...iconProps}
                                        title="Sort ascending"
                                        className={getIconClassName(
                                            'asc',
                                            isSorted,
                                        )}
                                    />
                                    <ChevronDownIcon
                                        {...iconProps}
                                        title="Sort descending"
                                        className={getIconClassName(
                                            'desc',
                                            isSorted,
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </>
            </th>
        );
    });
};

export default function Table<TData, TValue = unknown>({
    data,
    columns = [],
    page = { status: false, pageSize: 100, showResults: true, numSlots: 7 },
    className = '',
    onRowsSelected,
    loading = false,
    height = undefined,
    rowHeight,
    draggable = false,
    onColumnDragged,
    primaryField,
}: TableProps<TData>) {
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: page.pageSize || 100,
    });

    const [columnsExceedScreen, setColumnsExceedScreen] = useState(false);

    const [rowSelection, setRowSelection] = useState({});
    const [updatedColumns, setUpdatedColumns] =
        useState<ColumnDef<TData, TValue>[]>(columns);

    useEffect(() => {
        setUpdatedColumns(columns);
    }, [columns]);

    const tableInstance = useReactTable<TData>({
        data: useMemo(() => data, [data]),
        columns: useMemo(() => {
            if (draggable) {
                return updatedColumns.map(col => ({
                    ...col,
                    enableSorting: col.enableSorting ?? col.sortable ?? false,
                }));
            }
            return columns.map(col => ({
                ...col,
                enableSorting: col.enableSorting ?? col.sortable ?? false,
            }));
        }, [updatedColumns, columns]),
        defaultColumn: {
            size: 200,
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getRowId: (row: TData) => {
            const selectRow = tableInstance
                .getHeaderGroups()[0]
                .headers.find(header => header.id === 'select');

            if (selectRow) {
                const selectionField: string = (
                    selectRow.column.columnDef as MyColumnDef<TData, unknown>
                ).accessorKey;
                if (selectionField !== 'index') {
                    return (row as any)[selectionField];
                }
            }
        },
        getPaginationRowModel: page.status
            ? getPaginationRowModel()
            : undefined,
        onPaginationChange: setPagination,
        onRowSelectionChange: (updater: any) => {
            setRowSelection(currentRowSelection => {
                const newRowSelection = updater(currentRowSelection);
                if (onRowsSelected) {
                    const selectedRows: TData[] = Object.keys(newRowSelection)
                        .filter(key => newRowSelection[key])
                        .map(key => key) as any[];
                    onRowsSelected(selectedRows);
                }
                return newRowSelection;
            });
        },
        state: {
            pagination,
            rowSelection,
        },
    });

    const { rows } = tableInstance.getRowModel();

    const enableRowSelelction = tableInstance
        .getHeaderGroups()[0]
        .headers.some(header => header.id === 'select');

    const visibleColumns = tableInstance.getVisibleLeafColumns();

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: () => (rowHeight ? parseInt(rowHeight, 10) : 33),
        getScrollElement: () => tableContainerRef.current,
        measureElement:
            typeof window !== 'undefined' &&
            navigator.userAgent.indexOf('Firefox') === -1
                ? element => element?.getBoundingClientRect().height
                : undefined,
        overscan: 5,
    });

    const hasNoData = tableInstance.getRowModel().rows.length === 0;

    const checkColumnsExceedScreen = useCallback(() => {
        if (tableContainerRef.current) {
            const containerWidth = tableContainerRef.current.clientWidth;
            const totalColumnsWidth = visibleColumns.reduce(
                (total, column) => total + column.getSize(),
                0,
            );
            setColumnsExceedScreen(totalColumnsWidth > containerWidth);
        }
    }, [visibleColumns]);

    useEffect(() => {
        checkColumnsExceedScreen();
        window.addEventListener('resize', checkColumnsExceedScreen);
        return () => {
            window.removeEventListener('resize', checkColumnsExceedScreen);
        };
    }, [columns, checkColumnsExceedScreen]);

    if (loading) {
        return (
            <div className="alli-flex alli-h-full alli-w-full alli-items-center alli-justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    const moveColumn = (dragIndex: number, hoverIndex: number) => {
        const reorderedColumns = [...updatedColumns];
        const [draggedColumn] = reorderedColumns.splice(dragIndex, 1);
        reorderedColumns.splice(hoverIndex, 0, draggedColumn);
        setUpdatedColumns(reorderedColumns);
        if (onColumnDragged) {
            onColumnDragged(reorderedColumns);
        }
    };

    return (
        <>
            <div
                data-testid="Table"
                ref={tableContainerRef}
                className={cn(
                    'alli-w-full alli-overflow-auto alli-bg-white',
                    className,
                )}
                style={{
                    ...(height
                        ? {
                              height: height,
                          }
                        : {}),
                }}>
                <table
                    className={cn('alli-min-w-full alli-divide-gray-700', {
                        'alli-grid': columnsExceedScreen || height,
                    })}>
                    <thead
                        className={cn(
                            'alli-border-b alli-border-t alli-border-gray-400 alli-bg-gray-100',
                            {
                                'alli-sticky alli-top-0 alli-z-10 alli-grid':
                                    height,
                            },
                        )}>
                        {tableInstance.getHeaderGroups().map(headerEl => (
                            <tr
                                key={headerEl.id}
                                className={cn({
                                    'alli-flex alli-w-full': height,
                                })}>
                                {renderHeaders(
                                    headerEl.headers as HeaderType[],
                                    tableInstance as {
                                        getIsAllRowsSelected: () => any;
                                        getIsSomeRowsSelected: () => any;
                                        getToggleAllRowsSelectedHandler: () => any;
                                    },
                                    enableRowSelelction as boolean,
                                    tableContainerRef as any,
                                    moveColumn as any,
                                    draggable as boolean,
                                )}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        className={cn(
                            'alli-divide-y alli-divide-gray-400 alli-bg-white',
                            {
                                'alli-relative alli-grid': height,
                            },
                        )}
                        style={{
                            ...(height
                                ? {
                                      height: `${rowVirtualizer.getTotalSize()}px`,
                                  }
                                : {}),
                        }}>
                        {hasNoData ? (
                            <tr
                                className={cn(
                                    'alli-w-full alli-justify-center',
                                    {
                                        'alli-flex': columnsExceedScreen,
                                    },
                                )}>
                                <td
                                    colSpan={
                                        tableInstance.getHeaderGroups()[0]
                                            .headers.length
                                    }
                                    className="alli-p-6 alli-text-center alli-text-sm alli-font-medium alli-text-blue-gray-700">
                                    No data found
                                </td>
                            </tr>
                        ) : height ? (
                            rowVirtualizer.getVirtualItems().map(virtualRow => {
                                const row = rows[virtualRow.index];
                                return (
                                    <tr
                                        data-index={virtualRow.index}
                                        ref={node =>
                                            rowVirtualizer.measureElement(node)
                                        }
                                        key={row.id}
                                        className="alli-absolute alli-flex alli-w-full alli-items-center"
                                        style={{
                                            transform: `translateY(${virtualRow.start}px)`,
                                            ...(rowHeight && {
                                                height: `${rowHeight}`,
                                            }),
                                        }}>
                                        {row
                                            .getVisibleCells()
                                            .map(
                                                (
                                                    cell: Cell<TData, unknown>,
                                                ) => {
                                                    const titleText = cell
                                                        .getContext()
                                                        .getValue();

                                                    return (
                                                        <td
                                                            data-testid={`table-column-${cell.id}`}
                                                            key={cell.id}
                                                            style={{
                                                                width: cell.column.getSize(),
                                                                ...getCommonStickyStyles(
                                                                    cell.column,
                                                                    tableContainerRef,
                                                                ),
                                                            }}
                                                            className={cn(
                                                                'alli-sm:pl-6 alli-flex alli-h-full alli-items-center alli-whitespace-nowrap alli-bg-white alli-px-3 alli-py-4 alli-text-sm alli-font-medium alli-text-gray-700',
                                                                {
                                                                    'alli-text-blue-gray-700':
                                                                        (
                                                                            cell
                                                                                .column
                                                                                .columnDef as MyColumnDef<
                                                                                TData,
                                                                                unknown
                                                                            >
                                                                        )
                                                                            ?.id ===
                                                                        primaryField,
                                                                },
                                                                {
                                                                    'alli-flex':
                                                                        columnsExceedScreen,
                                                                },
                                                            )}
                                                            title={
                                                                titleText as string
                                                            }>
                                                            {cell.column
                                                                .columnDef
                                                                .id ===
                                                            'select' ? (
                                                                <IndeterminateCheckbox
                                                                    {...{
                                                                        checked:
                                                                            row.getIsSelected(),
                                                                        disabled:
                                                                            !row.getCanSelect(),
                                                                        indeterminate:
                                                                            row.getIsSomeSelected(),
                                                                        onChange:
                                                                            row.getToggleSelectedHandler(),
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="alli-text-ellipsis alli-text-nowrap">
                                                                    {flexRender(
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .cell,
                                                                        cell.getContext(),
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                },
                                            )}
                                    </tr>
                                );
                            })
                        ) : (
                            tableInstance
                                .getRowModel()
                                .rows.map((row: Row<TData>) => (
                                    <tr
                                        key={row.id}
                                        data-testid="Table-row"
                                        className={cn(
                                            'alli-w-full alli-items-center',
                                            {
                                                'alli-flex':
                                                    columnsExceedScreen,
                                            },
                                        )}
                                        style={{
                                            ...(rowHeight && {
                                                height: `${rowHeight}`,
                                            }),
                                        }}>
                                        {row.getVisibleCells().map(cell => {
                                            const titleText = cell
                                                .getContext()
                                                .getValue();
                                            return (
                                                <td
                                                    data-testid={`table-column-${cell.id}`}
                                                    key={cell.id}
                                                    style={{
                                                        width: cell.column.getSize(),
                                                        ...getCommonStickyStyles(
                                                            cell.column,
                                                            tableContainerRef,
                                                        ),
                                                    }}
                                                    className={cn(
                                                        'alli-sm:pl-6 alli-whitespace-nowrap alli-bg-white alli-px-3 alli-py-4 alli-text-sm alli-font-medium alli-text-gray-700',
                                                        {
                                                            'alli-text-blue-gray-700':
                                                                (
                                                                    cell.column
                                                                        .columnDef as MyColumnDef<
                                                                        TData,
                                                                        unknown
                                                                    >
                                                                )?.id ===
                                                                primaryField,
                                                        },
                                                        {
                                                            'alli-flex':
                                                                columnsExceedScreen,
                                                        },
                                                    )}
                                                    title={titleText as string}>
                                                    {cell.column.columnDef
                                                        .id === 'select' ? (
                                                        <IndeterminateCheckbox
                                                            {...{
                                                                checked:
                                                                    row.getIsSelected(),
                                                                disabled:
                                                                    !row.getCanSelect(),
                                                                indeterminate:
                                                                    row.getIsSomeSelected(),
                                                                onChange:
                                                                    row.getToggleSelectedHandler(),
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="alli-text-ellipsis alli-text-nowrap">
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext(),
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                        )}
                    </tbody>
                </table>
            </div>
            {page.status && !hasNoData && (
                <Pagination
                    perPage={tableInstance.getState().pagination.pageSize}
                    startPage={1}
                    currentPage={
                        tableInstance.getState().pagination.pageIndex + 1
                    }
                    numSlots={page.numSlots || 7}
                    showResults={page.showResults || true}
                    totalItems={tableInstance.getRowCount()}>
                    {({ className, pageParam, children }) => (
                        <span
                            className={cn('alli-cursor-pointer', className)}
                            onClick={() =>
                                tableInstance.setPageIndex(
                                    Number(pageParam) - 1,
                                )
                            }>
                            {children}
                        </span>
                    )}
                </Pagination>
            )}
        </>
    );
}

function IndeterminateCheckbox({
    indeterminate,
    className = 'alli-h-4 alli-w-4 alli-rounded alli-border-gray-700 alli-text-blue-600 focus:alli-ring-blue-600',
    ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
    const ref = useRef<HTMLInputElement>(null!);

    useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate]);

    return (
        <input
            type="checkbox"
            ref={ref}
            className={className + ' alli-cursor-pointer'}
            {...rest}
        />
    );
}
